import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { EventEmitter, Injectable, Output } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { environment } from 'environments/environment';
import { BehaviorSubject, interval, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

interface UserRequestForm {
  business: string;
  name: string;
  email: string;
  phone: string;
}

interface UserCredentials {
  email: string;
  password: string;
  otp?: string;
  resend?: boolean;
}

interface TokenResponse {
  token: string;
  ttl: number;
}

interface ResetPasswordForm {
  email: string;
}

interface ChangePasswordForm {
  curPassword: string;
  newPassword: string;
}

interface TokenBusinessData {
  id: string;
  name: string;
  address_1: string;
  address_2: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  email: string;
  account_owner_id: string;
  streamline_mode: boolean;
  view_global_devices: boolean;
  global2FA: boolean;
  allow_tech_subs: boolean;
  isLegacy: boolean;
  app: string;
  modified: string;
  created: string;
  segment_id: number;
}

interface TokenData {
  id: string;
  email:string;
  isLegacy: boolean;
  app: string;
  name: string;
  bus: string;
  seg: number;
  roles: string[];
  reset: number;
  pushToken: string;
  business: TokenBusinessData;
  iat: number;
  exp: number;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  _currentUser: BehaviorSubject<TokenData> = new BehaviorSubject<TokenData>(null);
  _currentBusiness: BehaviorSubject<TokenBusinessData> = new BehaviorSubject<TokenBusinessData>(null);
  streamline: BehaviorSubject<any> = new BehaviorSubject<any>(false);
  private authToken: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  private jwtHelper: JwtHelperService = new JwtHelperService();
  private refreshTokenInterval: Subscription;
  @Output() userLoggedIn: EventEmitter<any> = new EventEmitter();

  constructor(private http: HttpClient, private router: Router) { }

  signupRequest(userRequestForm: UserRequestForm): Observable<string> {
    return this.http.post(
      environment.apiUrl + '/signup_request',
      userRequestForm,
      { responseType: 'text' },
    );
  }

  login(userCredentials: UserCredentials, remember: boolean): Observable<string | void> {
    window.sessionStorage.clear();
    return this.http
      .post<TokenResponse>(
        environment.apiUrl + '/users/login?remember=' + remember,
        userCredentials,
        { withCredentials: true },
      )
      .pipe(
        map((res: any) => {
          if (res.message === 'OTP required') {
            return 'otp';
          } else {
            const user = this.jwtHelper.decodeToken(res.token);
            const error = this.validateUser(user);
            if (error) {
              throw error;
            }

            this.authToken.next(res.token);
            this._currentUser.next(user);
            this._currentBusiness.next(user.business);
            this.setupRefreshTokenInterval(res.ttl);
            this.userLoggedIn.emit(res.token);
          }
        }),
      );
  }

  logout(): void {
    this.http
      .post(environment.apiUrl + '/users/logout', null, {
        withCredentials: true,
      })
      .pipe(
        map(() => {
          this.authToken.next(null);
          this._currentUser.next(null);
          this._currentBusiness.next(null);
          this.refreshTokenInterval.unsubscribe();
          this.refreshTokenInterval = null;
        }),
      ).subscribe(() => {
        this.router.navigate(['/login']);
      });

    window.sessionStorage.clear();
  }

  resetPassword(resetPasswordForm: ResetPasswordForm): Observable<string> {
    return this.http.post(
      environment.apiUrl + '/users/reset_password',
      resetPasswordForm,
      { responseType: 'text' },
    );
  }

  changePassword(changePasswordForm: ChangePasswordForm): Observable<string> {
    return this.http.post(
      environment.apiUrl + '/users/change_password',
      changePasswordForm,
      { responseType: 'text' },
    );
  }

  refreshToken(): Observable<void> {
    return this.http
      .post<TokenResponse>(environment.apiUrl + '/users/refresh_token', null, {
        withCredentials: true,
      })
      .pipe(
        map((res) => {
          const user = this.jwtHelper.decodeToken(res.token);

          const error = this.validateUser(user);
          if (error)
            throw error;

          this.authToken.next(res.token);
          this._currentUser.next(user);
          this._currentBusiness.next(user.business);
          this.setupRefreshTokenInterval(res.ttl);
        }),
      );
  }

  sendVerificationEmail(email: string): Observable<any> {
    return this.http.post(
      environment.apiUrl + '/users/resend-verification?email=' + encodeURIComponent(email),
      null,
    );
  }

  sendConfirmationEmail(email: string): Observable<any> {
    return this.http.post(
      `${environment.apiUrl}/users/resend-business-verification?email=${encodeURIComponent(email)}`, null
    );
  }

  isAdmin(): boolean {
    return this.currentUser?.roles.includes('entadm') || this.currentUser?.roles.includes('busadm');
  }

  getToken(): string {
    return this.authToken.value;
  }

  get currentUser(): TokenData {
    if (!this._currentUser.value) this.refreshToken();
    return this._currentUser.value;
  }

  set currentUser(val: TokenData) {
    this._currentUser.next(val);
  }

  get currentBusiness(): TokenBusinessData {
    return this._currentBusiness.value;
  }

  set currentBusiness(val: TokenBusinessData) {
    this._currentBusiness.next(val);
  }

  isStreamline(): any {
    return !!this.streamline.value;
  }

  validateBusinessId(id: string): any {
    if (id && id != 'null' && id != 'undefined')
      return true;
    else
      throw { error: 'L01', message: 'Invalid business ID.' };
  }

  isLoggedIn(): boolean {
    return this.authToken.value != null && !!this.currentUser;
  }

  private setupRefreshTokenInterval(sec: number): void {
    if (this.refreshTokenInterval) {
      return;
    }

    // Add a 10% time buffer
    const buffer = Math.floor(sec * 0.1);

    this.refreshTokenInterval = interval((sec - buffer) * 1000).subscribe(
      () => {
        this.refreshToken().subscribe();
      },
    );
  }

  private validateUser(user: any): any {
    if (!user.bus) {
      return {
        code: 401,
        message: 'User is not associated with a business.',
      };
    }

    if (
      user.roles.indexOf('entadm') === -1 &&
      user.roles.indexOf('busadm') === -1 &&
      user.roles.indexOf('busfin') === -1
    ) {
      return {
        code: 401,
        message:
          'At this time, only accounts with administrator privileges can access the portal. Please contact an administrator if you have any questions.',
      };
    }

    return null;
  }
}
